import { useState } from 'react'

import { useMutation } from '@redwoodjs/web'

import { toast } from 'src/components/Overlays/Toast/Toast'
import { CREATE_DOCUMENT, UPDATE_DOCUMENT } from 'src/graphql/documents.graphql'
import {
  DocumentCategory,
  DocumentUserStatus,
  FileContentType,
  UploadStatus,
} from 'src/graphql/types/documents'
import { appendCorrectFileExtension } from 'src/pages/ApplicantDetailsPage/tabs/DocumentsTab/components/UploadDocumentModal'

const fileContentTypeByFileType = {
  'image/png': FileContentType.PNG,
  'image/jpeg': FileContentType.JPG,
  'image/jpg': FileContentType.JPG,
  'image/tiff': FileContentType.TIF,
  'image/gif': FileContentType.GIF,
  'image/heic': FileContentType.HEIC,
  'application/pdf': FileContentType.PDF,
  'text/html': FileContentType.HTML,
  'application/msword': FileContentType.DOC,
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
    FileContentType.DOC,
  'application/zip': FileContentType.ZIP,
  'text/csv': FileContentType.CSV,
  'text/plain': FileContentType.TXT,
  'audio/wav': FileContentType.WAV,
  'audio/x-wav': FileContentType.WAV,
  'video/mp4': FileContentType.MP4,
  'application/vnd.ms-excel': FileContentType.XLS,
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet':
    FileContentType.XLS,
}

const useUploadDocument = (
  refetchQueries,
  applicantId,
  step = null,
  voe = null,
  category = DocumentCategory.OTHER
) => {
  const [isUploading, setIsUploading] = useState(false)

  const onError = () => {
    toast('Unable to update/create document, please try again.', 'error')
    setIsUploading(false)
  }

  /************ MUTATIONS ************/
  const [updateDocument] = useMutation(UPDATE_DOCUMENT, {
    refetchQueries: refetchQueries,
    onError,
  })

  const [createDocument] = useMutation(CREATE_DOCUMENT, {
    onError,
  })

  /************ MAIN HANDLER ************/
  const handleCreateDocuments = async (files, onDocumentsCreated) => {
    const documents = []
    let index = 0
    setIsUploading(true)

    files.map(async (file) => {
      const fileNameWithExtension = appendCorrectFileExtension(
        file.name,
        file.type
      )

      createDocument({
        variables: {
          input: {
            fileType: fileContentTypeByFileType[file.type],
            category: category,
            userStatus: DocumentUserStatus.COMPLETED,
            fileName: fileNameWithExtension,
            expirationDate: file?.expirationDate
              ? new Date(file.expirationDate)
              : null,
            applicantId,
            stepId: step ? step.stepId : null,
            voeId: voe ? voe.voeId : null,
          },
        },
        update: async (cache, { data }) => {
          const createDocument = data.createDocument
          const { uploadUrl } = createDocument

          try {
            const response = await fetch(uploadUrl, {
              method: 'PUT',
              body: file,
              headers: {
                'Content-Type': file.type,
              },
            })

            if (!response.ok) {
              throw new Error(`Upload failed with status: ${response.status}`)
            }

            updateDocument({
              variables: {
                id: createDocument.documentId,
                input: {
                  uploadStatus: UploadStatus.COMPLETED,
                },
              },
              update: (cache, { data }) => {
                const updateDocument = data.updateDocument
                documents.push(updateDocument.documentId)
                setIsUploading(false)
                if (onDocumentsCreated && index === files.length - 1) {
                  onDocumentsCreated(documents)
                }
                index++
              },
            })
          } catch (error) {
            console.error('Document upload to S3 failed:', error)
            toast('Document upload failed. Please try again.', 'error')
            setIsUploading(false)
          }
        },
      })
    })
  }

  return { handleCreateDocuments, isUploading }
}

export default useUploadDocument
